import React, { useEffect, useRef } from "react";
import { Application, Container, Sprite, Assets, Graphics } from "pixi.js";
import { ClientPokemon } from "@/lib/battle/entities/client-pokemon";
import {
  Battle_Action_Move,
  Direction,
  BattlePokemonData,
  PokemonType,
} from "types/battle-engine/battle";
import {
  GRID_WIDTH,
  GRID_HEIGHT,
  gridTileSize,
  gridMarginX,
  gridMarginY,
  MS_PER_TICK,
} from "types/battle-engine/constants";
import {
  getRaidIntroConfig,
  IntroAction,
  IntroActorConfig,
} from "./raid-intro-config";
import { RaidId, raidNameToId } from "game/data/raids";

interface RaidIntroSceneProps {
  raidName: string;
  onComplete: () => void;
  setOverlayText: (text: { title?: string; subtitle?: string } | null) => void;
}

// Helper to create mock data for actors
const createActorPokemonData = (
  actor: IntroActorConfig,
  index: number,
): BattlePokemonData => {
  return {
    id: 10000 + index,
    base_pokemon_name: actor.basePokemonName,
    name: actor.basePokemonName,
    level: 100, // Dummy
    pokemon_types: [PokemonType.NORMAL],
    hp: 100,
    max_hp: 100,
    ability_power: 10,
    resistance: 10,
    speed: 10,
    skills: [],
    scale: actor.scale || 1,
    weight: 1,
  };
};

export const RaidIntroScene: React.FC<RaidIntroSceneProps> = ({
  raidName,
  onComplete,
  setOverlayText,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<Application | null>(null);
  const worldRef = useRef<Container | null>(null);
  const pokemonsRef = useRef<Record<string, ClientPokemon>>({});

  useEffect(() => {
    let isCancelled = false;

    const raidId = raidNameToId(raidName);
    const config = getRaidIntroConfig(raidId);

    const init = async () => {
      if (!containerRef.current) return;

      // 1. Initialize Pixi App
      const app = new Application();
      await app.init({
        resizeTo: containerRef.current,
        backgroundAlpha: 0,
        roundPixels: true, // Enable round pixels for sharp rendering
      });

      if (isCancelled) {
        app.destroy();
        return;
      }

      containerRef.current.appendChild(app.canvas);
      appRef.current = app;

      // Initialize a black overlay for fades
      const fadeOverlay = new Graphics();
      fadeOverlay.rect(0, 0, app.screen.width, app.screen.height);
      fadeOverlay.fill({ color: 0x000000 });
      fadeOverlay.zIndex = 10000; // Above everything
      fadeOverlay.alpha = 0; // Transparent by default
      app.stage.addChild(fadeOverlay);
      (app as any)._fadeOverlay = fadeOverlay;

      // 2. Load Background
      try {
        const bgTexture = await Assets.load(
          `/assets/maps/${config.mapId}.webp`,
        );
        if (isCancelled) return;

        const bgSprite = Sprite.from(bgTexture);
        bgSprite.anchor.set(0.5);

        bgSprite.texture.baseTexture.scaleMode = "nearest"; // Set to nearest for pixel art

        // Map dimensions from Duel standards
        const mapWidth = 480;
        const mapHeight = 960;

        // Position at center of the reference map area
        bgSprite.x = mapWidth / 2;
        bgSprite.y = mapHeight / 2;

        // Scale to fit the reference area exactly
        bgSprite.width = mapWidth;
        bgSprite.height = mapHeight;

        bgSprite.zIndex = -1000; // Behind everything

        // Store for later
        (config as any)._bgSprite = bgSprite;
      } catch (e) {
        console.warn("Failed to load background:", e);
      }

      // 3. Initialize World Container
      const world = new Container();
      // Enable sorting
      world.sortableChildren = true;
      app.stage.addChild(world);
      worldRef.current = world;

      // Add background to world if loaded
      if ((config as any)._bgSprite) {
        world.addChild((config as any)._bgSprite);
      }

      // Calculate scale to fit screen like in Duel view
      const mapWidth = 480;
      const mapHeight = 960;

      const scaleX = app.screen.width / mapWidth;
      const scaleY = app.screen.height / mapHeight;
      const baseScale = Math.min(scaleX, scaleY, 1); // Standard scale capped at 1

      // Use configured initial zoom if provided
      const initialZoom = config.initialZoom || 1.0;
      const startScale = baseScale * initialZoom;

      world.scale.set(startScale);

      // Initial Camera Position
      // centerX is center of the reference map area
      const centerX = mapWidth / 2;

      // Use configured initial Camera Y (grid coord) or default center
      const startCameraY =
        config.initialCameraY !== undefined
          ? config.initialCameraY
          : GRID_HEIGHT / 2;

      // Calculate pixel Y position based on grid coordinate
      const startCameraYPixels = gridMarginY + startCameraY * gridTileSize;

      world.x = app.screen.width / 2 - centerX * startScale;
      world.y = app.screen.height / 2 - startCameraYPixels * startScale;

      // 4. Initialize Actors
      // The config has `actors` list for asset loading/definitions.
      // But they spawn via events.
      // However, to ensure smooth playback, we might want to load them all upfront.

      const actorsMap: Record<string, ClientPokemon> = {};

      const actorPromises = config.actors.map(async (actorConfig, i) => {
        const battleData = createActorPokemonData(actorConfig, i);

        // Determine spawn position
        // If initialPosition is provided, use it. Otherwise far away.
        const spawnPos = actorConfig.initialPosition
          ? {
              x: actorConfig.initialPosition.x,
              y: actorConfig.initialPosition.y,
            }
          : { x: -100, y: -100 };

        const pokemon = new ClientPokemon(
          battleData,
          spawnPos,
          "A",
          app,
          world,
          true, // hideUI
        );

        await pokemon.load();

        // If initialPosition provided, make visible and set direction immediately
        if (actorConfig.initialPosition) {
          pokemon.container.visible = true;
          if (actorConfig.initialPosition.direction) {
            pokemon.setAnimation(
              ["Idle"],
              actorConfig.initialPosition.direction,
            );
          }
        } else {
          // Hide initially until SPAWN event
          pokemon.container.visible = false;
        }

        actorsMap[actorConfig.id] = pokemon;
      });

      await Promise.all(actorPromises);
      if (isCancelled) return;

      pokemonsRef.current = actorsMap;

      // 5. Run Script
      await runSequence(
        config.sequence,
        actorsMap,
        app,
        world,
        baseScale,
        config,
      );

      if (!isCancelled) {
        onComplete();
      }
    };

    const runSequence = async (
      actions: IntroAction[],
      pokemons: Record<string, ClientPokemon>,
      app: Application,
      world: Container,
      baseScale: number,
      config: any,
    ) => {
      for (const action of actions) {
        if (isCancelled) break;
        await executeAction(action, pokemons, app, world, baseScale, config);
      }
    };

    const executeAction = async (
      action: IntroAction,
      pokemons: Record<string, ClientPokemon>,
      app: Application,
      world: Container,
      baseScale: number,
      config: any,
    ) => {
      switch (action.type) {
        case "WAIT":
          await new Promise((r) => setTimeout(r, action.waitMs || 500));
          break;

        case "SIMULTANEOUS":
          if (action.actions) {
            await Promise.all(
              action.actions.map((a) =>
                executeAction(a, pokemons, app, world, baseScale, config),
              ),
            );
          }
          break;

        case "FADE_IN": {
          const duration = action.duration || 1000;
          const overlay = (app as any)._fadeOverlay;
          if (!overlay) break;

          // Start from solid black
          overlay.alpha = 1;

          await new Promise<void>((resolve) => {
            const startTime = performance.now();
            const animate = () => {
              if (isCancelled) {
                resolve();
                return;
              }
              const now = performance.now();
              const progress = Math.min(1, (now - startTime) / duration);
              overlay.alpha = 1 - progress;

              if (progress < 1) {
                requestAnimationFrame(animate);
              } else {
                resolve();
              }
            };
            requestAnimationFrame(animate);
          });
          break;
        }

        case "FADE_OUT": {
          const duration = action.duration || 1000;
          const overlay = (app as any)._fadeOverlay;
          if (!overlay) break;

          await new Promise<void>((resolve) => {
            const startTime = performance.now();
            const animate = () => {
              if (isCancelled) {
                resolve();
                return;
              }
              const now = performance.now();
              const progress = Math.min(1, (now - startTime) / duration);
              overlay.alpha = progress;

              if (progress < 1) {
                requestAnimationFrame(animate);
              } else {
                resolve();
              }
            };
            requestAnimationFrame(animate);
          });
          break;
        }

        case "SPAWN":
          if (action.actorId && action.position) {
            const pokemon = pokemons[action.actorId];
            if (pokemon) {
              // Teleport to position
              // We need to manually set position as ClientPokemon expects applyMove or initial pos
              // accessing container directly
              const x = action.position.x * gridTileSize + gridMarginX;
              const y = action.position.y * gridTileSize + gridMarginY;
              pokemon.container.x = x;
              pokemon.container.y = y;
              pokemon.container.zIndex = y;
              pokemon.Position.x = x; // Update internal position tracker
              pokemon.Position.y = y;

              if (action.direction) {
                pokemon.setAnimation(["Idle"], action.direction);
              }

              pokemon.container.visible = true;

              // If animation provided
              if (action.animationName) {
                pokemon.setAnimation(
                  [action.animationName],
                  action.direction || Direction.S,
                );
              }
            }
          }
          break;

        case "MOVE":
          if (action.actorId && action.destination) {
            const pokemon = pokemons[action.actorId];
            if (pokemon) {
              const durationMs = action.duration || 1000;
              const ticks = Math.ceil(durationMs / MS_PER_TICK);

              const moveAction: Battle_Action_Move = {
                type: "MOVE",
                to: action.destination,
                total_ticks: ticks,
                remaining_ticks: ticks,
                starting_tick: 0,
                log_info: {},
              };

              await pokemon.applyMove(moveAction);
            }
          }
          break;

        case "ANIMATION":
          if (action.actorId && action.animationName) {
            const pokemon = pokemons[action.actorId];
            if (pokemon) {
              const dir = action.direction || pokemon.CurrentDir;
              if (action.loop === false) {
                pokemon.setAnimationOnce(action.animationName, dir);
              } else {
                pokemon.setAnimation([action.animationName], dir);
              }
            }
          }
          break;

        case "CAMERA_MOVE":
          if (action.cameraEnd) {
            const startY = action.cameraStart ? action.cameraStart.y : null;
            const endY = action.cameraEnd.y;
            const duration = action.duration || 1000;
            const scale = world.scale.y; // Use current world scale

            const startTime = performance.now();

            // Helper to get world Y for a grid Y
            // We want the target Grid Y to be in the CENTER of the screen
            const getContainerY = (gridY: number) => {
              // Formula: screenCenter = worldY + (gridY * tileSize + margin) * scale
              // worldY = screenCenter - (gridY * tileSize + margin) * scale
              return (
                app.screen.height / 2 -
                (gridY * gridTileSize + gridMarginY) * scale
              );
            };

            const targetWorldY = getContainerY(endY);
            // If startY is not provided, use current world.y
            // Note: If the world was initialized centered on something else, this transition will be smooth from there.
            const initialWorldY =
              startY !== null ? getContainerY(startY) : world.y;

            // Snap to start if explicitly provided
            if (startY !== null) {
              world.y = initialWorldY;
            }

            await new Promise<void>((resolve) => {
              const animate = () => {
                if (isCancelled) {
                  resolve();
                  return;
                }
                const now = performance.now();
                const progress = Math.min(1, (now - startTime) / duration);

                // Ease in-out? or linear. Linear is usually better for camera pans unless specified.
                world.y =
                  initialWorldY + (targetWorldY - initialWorldY) * progress;

                if (progress < 1) {
                  requestAnimationFrame(animate);
                } else {
                  resolve();
                }
              };
              requestAnimationFrame(animate);
            });
          }
          break;

        case "CAMERA_ZOOM":
          if (action.zoomEnd !== undefined) {
            const startZoom = action.zoomStart || world.scale.x / baseScale;
            const endZoom = action.zoomEnd;
            const duration = action.duration || 1000;
            const startTime = performance.now();

            // We need to zoom around the CENTER of the screen
            // Current center in World Coords:
            // worldX = screenCenterX - centerGridX * tileSize * currentScale

            // Actually, we just need to maintain the current "Camera Center" point at the center of the screen
            // The camera center is implicitly defined by world.x/y
            // Let's deduce the current grid center from world.x/y
            // screenCenterX = world.x + (gridCenterX * tileSize + margin) * currentScale
            // (screenCenterX - world.x) / currentScale = gridCenterX * tileSize + margin
            // currentCameraGridCenterY = ((screenCenterY - world.y) / currentScale - margin) / tileSize

            const currentScale = world.scale.x;
            const centerGridY =
              ((app.screen.height / 2 - world.y) / currentScale - gridMarginY) /
              gridTileSize;
            const centerGridX =
              ((app.screen.width / 2 - world.x) / currentScale - gridMarginX) /
              gridTileSize;

            await new Promise<void>((resolve) => {
              const animate = () => {
                if (isCancelled) {
                  resolve();
                  return;
                }
                const now = performance.now();
                const progress = Math.min(1, (now - startTime) / duration);

                // Interpolate zoom
                const currentZoom =
                  startZoom + (endZoom - startZoom) * progress;
                const newScale = baseScale * currentZoom;

                world.scale.set(newScale);

                // Adjust world position to keep the same center point
                world.x =
                  app.screen.width / 2 -
                  (centerGridX * gridTileSize + gridMarginX) * newScale;
                world.y =
                  app.screen.height / 2 -
                  (centerGridY * gridTileSize + gridMarginY) * newScale;

                if (progress < 1) {
                  requestAnimationFrame(animate);
                } else {
                  resolve();
                }
              };
              requestAnimationFrame(animate);
            });
          }
          break;

        case "TEXT":
          setOverlayText({
            title: action.title || config.title || "",
            subtitle: action.subtitle || "",
          });
          if (action.duration) {
            await new Promise((r) => setTimeout(r, action.duration));
          }
          break;
      }
    };

    init();

    return () => {
      isCancelled = true;
      if (appRef.current) {
        // Clean up the app but don't destroy global PixiJS resources (false first arg)
        // This prevents corrupting the main Raid.tsx PixiJS app's shared resources
        try {
          appRef.current.canvas?.remove();
          appRef.current.stage?.destroy({ children: true });
          appRef.current.destroy(false, { children: true, texture: false });
        } catch (e) {
          console.warn("[RaidIntroScene] Cleanup error:", e);
        }
        appRef.current = null;
      }
    };
  }, [raidName]); // Re-run if raidName changes

  return <div ref={containerRef} className="w-full h-full" />;
};
